/* eslint-disable @next/next/no-img-element */

import * as Tooltip from "@radix-ui/react-tooltip";
import type {
  IGrantUpdateStatus,
  IMilestoneCompleted,
  IProjectImpactStatus,
} from "@show-karma/karma-gap-sdk/core/class/karma-indexer/api/types";
import { type FC, useEffect, useState } from "react";
import type { Hex } from "viem";
import EthereumAddressToENSAvatar from "@/components/EthereumAddressToENSAvatar";
import { useENS } from "@/store/ens";
import { formatDate } from "@/utilities/formatDate";
import { VerificationsDialog } from "./VerificationsDialog";

interface VerifiedBadgeProps {
  // V2: Simple boolean verification
  isVerified?: boolean;
  // Legacy: Array of verification records
  verifications?: IMilestoneCompleted[] | IGrantUpdateStatus[] | IProjectImpactStatus[];
  title: string;
}

const _BlockieTooltip = ({
  address,
  date,
  reason,
}: {
  address: Hex;
  date: Date;
  reason?: string;
}) => {
  const { ensData, populateEns } = useENS();

  useEffect(() => {
    populateEns([address]);
  }, [address, populateEns]);

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0.5}>
        <Tooltip.Trigger asChild>
          <div>
            <EthereumAddressToENSAvatar
              address={address}
              className="h-8 w-8 min-h-8 min-w-8 rounded-full ring-2 ring-white dark:ring-gray-800"
            />
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="TooltipContent bg-brand-darkblue rounded-lg text-white p-3 max-w-[360px]"
            sideOffset={5}
            side="bottom"
          >
            <div>
              <div>
                <p className="text-xs font-bold truncate">{ensData[address]?.name || address}</p>
                <p className="text-xs font-normal">on {formatDate(date)}</p>
              </div>
              <p className="text-xs font-normal mt-1">{reason}</p>
            </div>
            <Tooltip.Arrow className="TooltipArrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export const VerifiedBadge: FC<VerifiedBadgeProps> = ({ isVerified, verifications, title }) => {
  // V2: If isVerified is true, just show a simple verified badge
  if (isVerified === true) {
    return (
      <div className="flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 dark:bg-green-900">
        <span className="text-sm font-medium text-green-700 dark:text-green-300">Verified</span>
      </div>
    );
  }

  // Legacy: Array-based verifications
  if (!verifications || verifications.length === 0) return null;

  return <VerifiedBadgeLegacy verifications={verifications} title={title} />;
};

// Legacy component for array-based verifications
const VerifiedBadgeLegacy: FC<{
  verifications: IMilestoneCompleted[] | IGrantUpdateStatus[] | IProjectImpactStatus[];
  title: string;
}> = ({ verifications, title }) => {
  const [orderedSort, setOrderedSort] = useState<
    (IMilestoneCompleted | IGrantUpdateStatus | IProjectImpactStatus)[]
  >([]);

  const getUniqueVerifications = (
    verifications: IMilestoneCompleted[] | IGrantUpdateStatus[] | IProjectImpactStatus[]
  ) => {
    // get unique and by last date
    const uniqueVerifications: Record<
      Hex,
      IMilestoneCompleted | IGrantUpdateStatus | IProjectImpactStatus
    > = {};
    verifications.forEach((verification) => {
      if (!verification.attester) return;
      if (!uniqueVerifications[verification.attester]) {
        uniqueVerifications[verification.attester] = verification;
      } else if (uniqueVerifications[verification.attester].createdAt < verification.createdAt) {
        uniqueVerifications[verification.attester] = verification;
      }
    });
    return Object.values(uniqueVerifications);
  };

  const [isOpenDialog, setIsOpenDialog] = useState(false);

  useEffect(() => {
    const uniques = getUniqueVerifications(verifications);

    // order by date
    const sorted = uniques.sort((a, b) => {
      if (new Date(a.createdAt).getTime() < new Date(b.createdAt).getTime()) return 1;
      if (new Date(a.createdAt).getTime() > new Date(b.createdAt).getTime()) return -1;
      return 0;
    });
    setOrderedSort(sorted);
  }, [verifications, getUniqueVerifications]);

  const openDialog = () => setIsOpenDialog(true);

  const closeDialog = () => setIsOpenDialog(false);

  const hasMore = orderedSort.length > 4;

  return (
    <div className="flex flex-row items-center gap-2 flex-1">
      <img alt="Verified Badge" src={"/icons/milestone-verified-badge.svg"} className="w-6 h-6" />
      <span className="text-sm font-semibold text-gray-700 dark:text-gray-400">Verified by</span>
      <VerificationsDialog
        verifications={orderedSort}
        isOpen={isOpenDialog}
        closeDialog={closeDialog}
        title={title}
      />
      <button className="ml-2 flex flex-row -space-x-1 flex-wrap" onClick={openDialog}>
        {orderedSort.slice(0, 4).map((verification) => (
          <EthereumAddressToENSAvatar
            key={verification.attester}
            address={verification.attester}
            className="h-8 w-8 min-h-8 min-w-8 rounded-full ring-2 ring-white dark:ring-gray-800"
          />
        ))}
        {hasMore ? (
          <div className="text-xs text-zinc-900 dark:text-white flex h-8 w-8 min-h-8 min-w-8 rounded-full ring-2 ring-zinc-200 dark:ring-gray-700 bg-white dark:bg-gray-800 justify-center items-center">
            + {orderedSort.length - 4}
          </div>
        ) : null}
      </button>
    </div>
  );
};
